import React, { useEffect, useState } from "react";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { Box, Typography } from "@mui/material";
import { usePagesTranslation } from "../locales";

const ICON_BEATS = 3;
const CTA_BEATS = 1;
const BEAT_MS = 1400;
const CYCLE = ICON_BEATS + CTA_BEATS;

function prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * In-phone store banner: icon + app name for three beats, then Play Now once.
 * Click opens the Google Play listing.
 */
export default function LandingPhoneStoreCta({
    iconUrl,
    appName,
    playUrl,
    ariaLabel,
    tone = "light",
}) {
    const { t } = usePagesTranslation();
    const [beat, setBeat] = useState(0);
    const dark = tone === "dark";
    const reduceMotion = prefersReducedMotion();
    const showPlayNow = !reduceMotion && beat % CYCLE >= ICON_BEATS;
    const playNowLabel = t("landing.play-now", "Play Now");

    useEffect(() => {
        if (reduceMotion) return undefined;
        const timer = window.setInterval(() => setBeat((n) => n + 1), BEAT_MS);
        return () => window.clearInterval(timer);
    }, [reduceMotion]);

    if (!playUrl || !iconUrl) return null;

    return (
        <Box
            component="a"
            href={playUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaLabel || playNowLabel}
            onClick={(event) => event.stopPropagation()}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                minHeight: 44,
                minWidth: 160,
                px: 1.25,
                py: 0.5,
                borderRadius: "12px",
                textDecoration: "none",
                pointerEvents: "auto",
                cursor: "pointer",
                color: dark ? "#F4F7FB" : "#0f1419",
                bgcolor: dark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.72)",
                border: dark
                    ? "1px solid rgba(255,255,255,0.16)"
                    : "1px solid rgba(15,23,42,0.08)",
                boxShadow: dark
                    ? "0 4px 14px rgba(2,6,23,0.28)"
                    : "0 2px 8px rgba(15,23,42,0.12)",
                "&:hover": {
                    bgcolor: dark ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.9)",
                },
                "&:focus-visible": {
                    outline: "2px solid #3BA666",
                    outlineOffset: 2,
                },
            }}
        >
            {showPlayNow ? (
                <>
                    <PlayArrowRoundedIcon
                        sx={{
                            fontSize: 28,
                            color: dark ? "#5EEAD4" : "#3BA666",
                            filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.25))",
                        }}
                    />
                    <Typography
                        component="span"
                        sx={{
                            fontWeight: 800,
                            fontSize: "0.92rem",
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            lineHeight: 1.2,
                        }}
                    >
                        {playNowLabel}
                    </Typography>
                </>
            ) : (
                <>
                    <Box
                        component="img"
                        src={iconUrl}
                        alt=""
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(15,23,42,0.18)",
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        component="span"
                        sx={{
                            fontWeight: 700,
                            fontSize: "0.92rem",
                            letterSpacing: "0.01em",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {appName}
                    </Typography>
                    {reduceMotion ? (
                        <PlayArrowRoundedIcon
                            sx={{
                                fontSize: 22,
                                color: dark ? "#5EEAD4" : "#3BA666",
                                ml: 0.25,
                            }}
                        />
                    ) : null}
                </>
            )}
        </Box>
    );
}
